import { useQuery } from '@tanstack/react-query'
import { request, getActiveTenantId, getActiveOrgEmail } from '@papan/api-client'

/**
 * email (lowercased) → the account's real display name, for members of the
 * active org.
 *
 * Access surfaces label a grantee from their email, through `nameFromEmail`,
 * which can only capitalise the local-part: an account genuinely called
 * "Kok 2" renders as "Kok2". That is a WRONG name rather than a shortened one,
 * and nothing on screen marks it as a guess — while the same card resolves its
 * CREATOR's real name, so one tooltip read "Kok Kok, Kok2", two naming rules
 * side by side.
 *
 * Lives in core-ui rather than beside its main consumers in `@papan/files`
 * because `@papan/share-links` needs it too, and files already depends on
 * share-links — importing the other way would close a cycle. Both depend on
 * core-ui, which already carries react-query and the api client.
 *
 * One shared query, not a fetch per surface: the drive listing, the Projects /
 * MiniApps cards, the Shared page and the share dialog all want the same list,
 * and a session opens several of them. Keyed by tenant + org-email so
 * switching identity can never serve one org's names inside another.
 *
 * Failure resolves to an EMPTY map rather than rejecting. The endpoint requires
 * membership, so a caller outside the org gets 403 and a personal tenant has no
 * members at all; both must land on the derived name — the existing behaviour —
 * not an error state on a decorative label. Recipients from outside the org
 * have no account anywhere, so they keep the derived name even on success:
 * a limit of the data, not of this hook.
 */
export const orgMemberNamesKey = () =>
  ['org-member-names', getActiveTenantId(), getActiveOrgEmail()] as const

export function useOrgMemberNames() {
  return useQuery<Map<string, string>>({
    queryKey: orgMemberNamesKey(),
    queryFn: async () => {
      const tenantId = getActiveTenantId()
      if (!tenantId) return new Map<string, string>()
      // Swallow only the answers that MEAN "no names here": 403 (not a member
      // of this org) and 404. Everything else — notably the transient 401 in
      // the window just after sign-in — is rethrown so react-query retries and
      // the map is not cached as a successful empty for `staleTime`. A blanket
      // catch turned that 401 into five minutes of email-derived guesses
      // ("Kok2") on every facepile, with no refetch, because a resolved promise
      // is a success no retry policy will revisit.
      const { members } = await request<{ members: Array<{ email: string; name?: string | null }> }>(
        `/api/orgs/${tenantId}/members`,
      ).catch((err: unknown) => {
        const status = (err as { status?: number } | null)?.status
        if (status === 403 || status === 404) return { members: [] as Array<{ email: string; name?: string | null }> }
        throw err
      })
      return new Map(
        members
          .filter((m) => m.name?.trim())
          .map((m) => [m.email.toLowerCase(), m.name!.trim()] as const),
      )
    },
    // Display names change about as often as people are renamed; five minutes
    // keeps a session's many surfaces on one fetch.
    staleTime: 5 * 60_000,
  })
}
